import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import config from '../../config/config.json';

function BroadcastEmailModal({ open, onClose, token }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      setError('Konu ve mesaj alanları zorunludur');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(
        `${config.api_base}/api/admin/broadcast-email`,
        {
          subject: subject.trim(),
          message: message.trim(),
          adminToken: token
        }
      );

      if (response.data.statusCode === 200) {
        setResult(response.data.data);
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setError(response.data.message || 'Email gönderilemedi');
      }
    } catch (err) {
      console.error('Broadcast email error:', err);
      setError('Email gönderilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setMessage('');
    setError('');
    setResult(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #40A2E3 0%, #2E86C1 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          paddingY: 3
        }}
      >
        <EmailIcon sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Toplu Email Gönder
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '14px' }}>
            Tüm kullanıcılara bilgilendirme mesajı gönder
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: 3 }}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert severity="success" sx={{ marginBottom: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 1 }}>
              Email gönderimi tamamlandı!
            </Typography>
            <Typography variant="body2">
              Toplam: {result.total} | Başarılı: {result.successful} | Başarısız: {result.failed}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 0.5 }}>
              Başarı Oranı: {result.successRate}
            </Typography>
          </Alert>
        )}

        <TextField
          label="Email Konusu"
          fullWidth
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={loading}
          sx={{ marginBottom: 2 }}
          placeholder="Örn: Yeni Özellik Duyurusu"
        />

        <TextField
          label="Mesaj İçeriği"
          fullWidth
          multiline
          rows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={loading}
          placeholder="Tüm kullanıcılara gönderilecek mesajı buraya yazın..."
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace'
            }
          }}
        />

        <Box
          sx={{
            marginTop: 2,
            padding: 2,
            backgroundColor: '#F5F7FA',
            borderRadius: '8px',
            borderLeft: '4px solid #40A2E3'
          }}
        >
          <Typography variant="body2" sx={{ color: '#6F7787', fontSize: '13px' }}>
            <strong>Not:</strong> Bu email tüm kayıtlı kullanıcılara gönderilecektir. Lütfen mesajınızı
            göndermeden önce dikkatlice kontrol edin. Email şablonu otomatik olarak uygulanacaktır.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 3, paddingTop: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#6F7787',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 600
          }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSend}
          disabled={loading || !subject.trim() || !message.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{
            background: 'linear-gradient(135deg, #40A2E3 0%, #2E86C1 100%)',
            color: 'white',
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 600,
            paddingX: 3,
            paddingY: 1.2,
            boxShadow: '0 4px 12px rgba(64, 162, 227, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2E86C1 0%, #236BA5 100%)'
            },
            '&:disabled': {
              background: '#E8ECF4',
              color: '#9CA3AF'
            }
          }}
        >
          {loading ? 'Gönderiliyor...' : 'Email Gönder'}
        </Button>
      </DialogActions>

      {loading && <LinearProgress />}
    </Dialog>
  );
}

export default BroadcastEmailModal;
